import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { useLocalMode } from './contexts/LocalModeContext'
import ErrorBoundary from './components/common/ErrorBoundary'
import LoadingSpinner from './components/common/LoadingSpinner'

// Layout Components
import Layout from './components/layout/Layout'
import NotesLayout from './components/layout/NotesLayout'

// Auth Pages
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import GoogleCallbackPage from './pages/auth/GoogleCallbackPage'

// Onboarding Page
import OnboardingPage from './pages/OnboardingPage'

// Protected Pages
import DashboardPage from './pages/DashboardPage'
import NotesPage from './pages/notes/NotesPage'
import CreateNotePage from './pages/notes/CreateNotePage'
import EditNotePage from './pages/notes/EditNotePage'
import ViewNotePage from './pages/notes/ViewNotePage'
import ProfilePage from './pages/ProfilePage'

// 404 Page
import NotFoundPage from './pages/NotFoundPage'

/**
 * Main Application Component
 * 
 * Handles routing, authentication state, local mode state, and provides the overall
 * application structure with protected routes and error boundaries.
 */
function App() {
  const { user, loading } = useAuth()
  const { isLocalMode } = useLocalMode()

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // If in local mode, show local-only routes
  if (isLocalMode && !user) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/notes/*" element={<NotesLayout />} />
          <Route path="/dashboard" element={
            <Layout>
              <DashboardPage />
            </Layout>
          } />
          <Route path="/" element={<Navigate to="/notes" replace />} />
          <Route path="*" element={<Navigate to="/notes" replace />} />
        </Routes>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          user ? (
            user.needsOnboarding ? <Navigate to="/onboarding" replace /> : <Navigate to="/notes" replace />
          ) : <LoginPage />
        } />
        <Route path="/register" element={
          user ? (
            user.needsOnboarding ? <Navigate to="/onboarding" replace /> : <Navigate to="/notes" replace />
          ) : <RegisterPage />
        } />
        <Route path="/forgot-password" element={
          user ? (
            user.needsOnboarding ? <Navigate to="/onboarding" replace /> : <Navigate to="/notes" replace />
          ) : <ForgotPasswordPage />
        } />
        <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />

        {/* Onboarding Route */}
        <Route path="/onboarding" element={
          user ? (
            user.needsOnboarding ? <OnboardingPage /> : <Navigate to="/notes" replace />
          ) : <Navigate to="/login" replace />
        } />
        
        {/* Protected Routes */}
        <Route path="/" element={
          user ? (
            user.needsOnboarding ? <Navigate to="/onboarding" replace /> : <Navigate to="/notes" replace />
          ) : <Navigate to="/login" replace />
        } />
        
        <Route path="/*" element={
          user ? (
            user.needsOnboarding ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <Routes>
                <Route path="/dashboard" element={
                  <Layout>
                    <DashboardPage />
                  </Layout>
                } />
                <Route path="/notes/*" element={<NotesLayout />} />
                <Route path="*" element={
                  <Layout>
                    <NotFoundPage />
                  </Layout>
                } />
              </Routes>
            )
          ) : (
            <Navigate to="/login" replace />
          )
        } />
      </Routes>
    </ErrorBoundary>
  )
}

export default App 